import { Box, Divider, Grid, Typography, useTheme } from '@mui/material';
import { platformNameConverter } from '../common-utilities';
import Sell from '@mui/icons-material/Sell';
import { AlertContainer, Container, DeveloperName } from './CoreDetails.styles';
import {
  AppName,
  PlatformIcon,
  PlatformIcons,
  ReviewStatusAlert,
} from '../AppDetails';

import {
  AppLogoImage,
  BenefitsStatusIcon,
  ContainedText,
  type IStylesOverrides,
} from '@orcha/micro-library-core-ui';
import type { IAppDetailsWidget } from '../common-types';
import { BenefitsBanner } from '../BenefitsBanner';
import { AssuredBanner } from '../AssuredBanner';
import type { ImageProps } from 'next/image';
import { ThresholdBanner, type ThresholdBannerProps } from '../ThresholdBanner';

export interface CoreDetailsProps extends IAppDetailsWidget {
  showCommissionableStatus?: boolean;
  fallbackLogoImageURL?: string;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  locale: string;
  assureEnabled?: boolean;
  configuration?: object & {
    thresholds: ThresholdBannerProps['thresholds'];
    styleOverrides?: IStylesOverrides;
  };
}

/**
 *  CoreDetails from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param param0
 * @param FlexibleImageComponent is required, this is the component that is used to display the image.
 * @returns
 */

export const CoreDetails = ({
  app,
  locale = 'en-GB',
  translations,
  showCommissionableStatus,
  fallbackLogoImageURL = '',
  FlexibleImageComponent,
  assureEnabled,
  configuration,
  ...props
}: CoreDetailsProps) => {
  const theme = useTheme();
  const { platforms, name, logoUrl, developerName, cost, appStatus, assured } =
    app ?? {};

  const { thresholds } = configuration ?? {};

  const ios = platforms?.find((p) =>
    p ? platformNameConverter(p.platformName) === 'ios' : undefined
  );
  const android = platforms?.find((p) =>
    p ? platformNameConverter(p.platformName) === 'android' : undefined
  );
  const webApp = platforms?.find((p) =>
    p ? platformNameConverter(p.platformName) === 'web' : undefined
  );
  const showAndroidWarning =
    (android && !android.isCurrentVersionReviewed) ?? false;
  const showIOSWarning = (ios && !ios.isCurrentVersionReviewed) ?? false;

  const getScoreSummaryForPlatform = () => {
    const relevantScoreSummaryIOS =
      platforms?.find((p) => p.platformName.toLowerCase().trim() === 'ios')
        ?.scoreSummary?.scoreValue ?? 0;

    if (relevantScoreSummaryIOS !== 0) return relevantScoreSummaryIOS;

    const relevantScoreSummaryAndroid =
      platforms?.find((p) => p.platformName.toLowerCase().trim() === 'android')
        ?.scoreSummary?.scoreValue ?? 0;

    if (relevantScoreSummaryAndroid !== 0) return relevantScoreSummaryAndroid;

    const relevantScoreSummaryWeb =
      platforms?.find((p) => p.platformName.toLowerCase().trim() === 'web app')
        ?.scoreSummary?.scoreValue ?? 0;

    if (relevantScoreSummaryWeb !== 0) return relevantScoreSummaryWeb;

    return 0;
  };

  const isAppCommissioned = appStatus?.status === 'Commissioned';

  const isAppCommissionable =
    !isAppCommissioned &&
    showCommissionableStatus &&
    appStatus?.status === 'Commissionable';

  const hideCost =
    (isAppCommissionable &&
      appStatus?.detailedStatus === 'CommissionableOnly') ||
    appStatus?.status === 'Promo' ||
    isAppCommissioned;

  return (
    <>
      <AlertContainer gap={1}>
        {showIOSWarning && (
          <ReviewStatusAlert>
            {translations.platformReviewWarnings.ios}
          </ReviewStatusAlert>
        )}

        {showAndroidWarning && (
          <ReviewStatusAlert>
            {translations.platformReviewWarnings.android}
          </ReviewStatusAlert>
        )}
      </AlertContainer>
      <Container {...props}>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            {assureEnabled && assured?.type != undefined && (
              <AssuredBanner
                bannerState={assured?.type}
                imageUrl={assured?.imageUrl}
                expiryDate={assured?.expiryDate}
                FlexibleImageComponent={FlexibleImageComponent}
                locale={locale}
              />
            )}
          </Grid>
          <Grid item>
            {thresholds && thresholds.length > 0 && (
              <ThresholdBanner
                value={getScoreSummaryForPlatform()}
                thresholds={thresholds}
                FlexibleImageComponent={FlexibleImageComponent}
              />
            )}
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs="auto">
              <Box
                sx={{
                  borderRadius: '10px',
                  height: 86,
                  width: 86,
                  overflow: 'hidden',
                }}
              >
                {name && logoUrl && (
                  <AppLogoImage
                    appName={name}
                    appLogoUrl={logoUrl}
                    fallbackLogoImageURL={fallbackLogoImageURL}
                    width="86"
                    height="86"
                    quality={100}
                    priority
                    data-testid="app-details-logo"
                    FlexibleImageComponent={FlexibleImageComponent}
                  />
                )}
              </Box>
            </Grid>
            <Grid
              item
              xs={7}
              md={10}
              container
              direction="column"
              justifyContent="space-between"
            >
              <Grid item>
                <AppName>{name}</AppName>
              </Grid>
              <Grid item>
                <div>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      display: 'inline',
                      color: 'grey.600',
                    }}
                    variant="body2"
                  >
                    {translations.developerLabel}
                  </Typography>
                  <DeveloperName>{developerName}</DeveloperName>
                </div>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
            wrap="wrap"
            spacing={2}
          >
            <Grid item xs={12} sm="auto">
              <PlatformIcons
                leadIconProps={{
                  titleAccess: translations.platformIconsTitle,
                }}
              >
                {ios && (
                  <PlatformIcon
                    platform="ios"
                    label={translations.platformLabels.ios}
                    iconProps={{
                      titleAccess: translations.platformIconTitle.ios,
                    }}
                  />
                )}
                {android && (
                  <PlatformIcon
                    platform="android"
                    label={translations.platformLabels.android}
                    iconProps={{
                      titleAccess: translations.platformIconTitle.android,
                    }}
                  />
                )}
                {webApp && (
                  <PlatformIcon
                    platform="web"
                    label={translations.platformLabels.web}
                    iconProps={{
                      titleAccess: translations.platformIconTitle.web,
                    }}
                  />
                )}
              </PlatformIcons>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(2),
                }}
              >
                {appStatus?.status === 'Promo' &&
                  appStatus.detailedStatus === 'FreeTrialPeriod' && (
                    <BenefitsStatusIcon
                      status="Promo"
                      label={translations.appCostLabels.freeTrialPeriod}
                      data-testid="app-details-promo-label"
                    />
                  )}
                {appStatus?.status === 'Promo' &&
                  appStatus.detailedStatus === 'DiscountedCost' && (
                    <BenefitsStatusIcon
                      status="Promo"
                      label={translations.appCostLabels.discountedCost}
                      data-testid="app-details-promo-label"
                    />
                  )}

                {isAppCommissionable && (
                  <BenefitsStatusIcon
                    status="Commissionable"
                    label={translations?.appCostLabels.commissionable ?? ''}
                    data-testid="app-details-commissionable-label"
                  />
                )}

                {isAppCommissioned && (
                  <BenefitsStatusIcon
                    status="Commissioned"
                    label={translations.appCostLabels.commissioned}
                    data-testid="app-details-commissioned-label"
                  />
                )}

                {isAppCommissionable && !hideCost && (
                  <Divider orientation="vertical" flexItem />
                )}

                {cost && !hideCost && (
                  <ContainedText
                    data-testid="app-details-cost-label"
                    icon={
                      <Sell
                        color="secondary"
                        titleAccess={translations.costIconTitle}
                        fontSize="small"
                      />
                    }
                  >
                    {cost}
                  </ContainedText>
                )}
              </Box>
            </Grid>
          </Grid>
          <Grid item>
            {appStatus?.status === 'Commissioned' && (
              <BenefitsBanner
                bannerState="Commissioned"
                title={translations.appBenefitsBanner.title.commissioned}
                body={translations.appBenefitsBanner.body.commissioned}
              />
            )}

            {showCommissionableStatus &&
              appStatus?.status === 'Commissionable' &&
              appStatus?.detailedStatus === 'Commissionable' && (
                <BenefitsBanner
                  bannerState="Commissionable"
                  title={translations.appBenefitsBanner.title.commissionable}
                  body={translations.appBenefitsBanner.body.commissionable}
                />
              )}

            {showCommissionableStatus &&
              appStatus?.status === 'Commissionable' &&
              appStatus?.detailedStatus === 'CommissionableOnly' && (
                <BenefitsBanner
                  bannerState="Commissionable"
                  title={
                    translations.appBenefitsBanner.title.commissionableOnly
                  }
                  body={translations.appBenefitsBanner.body.commissionableOnly}
                />
              )}

            {appStatus?.status === 'Promo' &&
              appStatus?.detailedStatus === 'FreeTrialPeriod' && (
                <BenefitsBanner
                  bannerState="Promo"
                  title={translations.appBenefitsBanner.title.freeTrialPeriod}
                  body={translations.appBenefitsBanner.body.freeTrialPeriod}
                />
              )}

            {appStatus?.status === 'Promo' &&
              appStatus?.detailedStatus === 'DiscountedCost' && (
                <BenefitsBanner
                  bannerState="Promo"
                  title={translations.appBenefitsBanner.title.discountedCost}
                  body={translations.appBenefitsBanner.body.discountedCost}
                />
              )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
